import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Observable, Subject as RxjsSubject } from "rxjs";
import { GEN_DRAFT_REPORT_TYPE, GEN_FINAL_REPORT_TYPE } from "../app.constants";
import { InspectionImageIdListItem } from "../_objects/inspection-image";
import { InspectionReport } from "../_objects/inspection-report";
import { SubjectLocation } from "../_objects/subject-model";

export class MoveImageIDLocation {
  imageIds: InspectionImageIdListItem[];
  location: SubjectLocation;

  constructor(imageIds: InspectionImageIdListItem[], location: SubjectLocation) {
    this.imageIds = imageIds;
    this.location = location;
  }
}

export class InspDataMultiUpdate {
  mark: number;
  title: string;
  tags: string[];
  comment: string;
  isNoChangeComment: boolean;

  constructor(mark: number, title: string, tags: string[], comment: string, isChangeComment: boolean) {
    this.mark = mark;
    this.title = title;
    this.tags = tags;
    this.comment = comment;
    this.isNoChangeComment = isChangeComment;
  }
}

export class ViewReport {
  reportId: string;
  version: number;

  constructor(reportId: string, version: number) {
    this.reportId = reportId;
    this.version = version;
  }
}

@Injectable({
  providedIn: "root"
})
export class InspectionDataManageService implements OnDestroy {
  private _currentReport: InspectionReport;
  private _selectedImageIds: InspectionImageIdListItem[] = [];
  private _imageIdInQueryParam: string;
  private _isReportView = false;
  private _shareReport = false;
  // 画像のロケーション変更イベント用
  private onMoveImageLocation$ = new RxjsSubject<MoveImageIDLocation>();

  // 検査画像フィルタリングイベント用
  private onFilterLocation$ = new BehaviorSubject<SubjectLocation>(null);

  // レポート再利用イベント用
  private onReuseReport$ = new RxjsSubject<string>();

  // 選択検査レポート変更イベント用
  private onChangeReport$ = new RxjsSubject<ViewReport>();

  // 検査画像一覧表示イベント用
  private onViewImages$ = new RxjsSubject();

  // 表示検査レポート変更イベント用
  private onViewReport$ = new RxjsSubject<ViewReport>();

  // レポートタブ更新イベント
  private onUpdateReportTab$ = new RxjsSubject<InspectionReport>();

  // レポート版数更新イベント
  private onReEditReport$ = new RxjsSubject<InspectionReport>();

  // ドラフトレポート生成イベント
  private onCreateDraftPdf$ = new RxjsSubject<GEN_DRAFT_REPORT_TYPE>();

  // ファイナルレポート生成イベント
  private onCreateFinalPdf$ = new RxjsSubject<GEN_FINAL_REPORT_TYPE>();

  // 過去レポート再取得イベント
  private onChangePastReportFilter$ = new RxjsSubject<string>();
  private _draftPdfIsAvailable$ = new BehaviorSubject<boolean>(false);

  get draftPdfIsAvailableObs(): Observable<boolean> {
    return this._draftPdfIsAvailable$.asObservable();
  }
  set draftPdfIsAvailable(value: boolean) {
    this._draftPdfIsAvailable$.next(value);
  }

  get currentReport(): InspectionReport {
    return this._currentReport;
  }

  set isReportView(reportView: boolean) {
    this._isReportView = reportView;
  }

  get isReportView(): boolean {
    return this._isReportView;
  }

  get filterLocation(): SubjectLocation {
    return this.onFilterLocation$.getValue();
  }

  get selectedImageIds(): InspectionImageIdListItem[] {
    return this._selectedImageIds;
  }

  set currentReport(value: InspectionReport) {
    this._currentReport = value;
  }

  get imageIdInQueryParam() {
    return this._imageIdInQueryParam;
  }

  set shareReport(value: boolean) {
    this._shareReport = value;
  }

  get shareReport() {
    return this._shareReport;
  }

  set imageIdInQueryParam(value: string) {
    this._imageIdInQueryParam = value;
  }

  constructor() {}

  ngOnDestroy(): void {
    this.onMoveImageLocation$.unsubscribe();
    this.onFilterLocation$.unsubscribe();
    this.onReuseReport$.unsubscribe();
    this.onChangeReport$.unsubscribe();
    this.onViewImages$.unsubscribe();
    this.onViewReport$.unsubscribe();
    this.onUpdateReportTab$.unsubscribe();
    this.onCreateDraftPdf$.unsubscribe();
    this.onCreateFinalPdf$.unsubscribe();
    this.onChangePastReportFilter$.unsubscribe();
  }

  selectImageId(imageIdItem: InspectionImageIdListItem): void {
    this._selectedImageIds.push(imageIdItem);
  }

  deselectImageId(deselectId: string): void {
    this._selectedImageIds = this._selectedImageIds.filter((imageId) => imageId.id !== deselectId);
  }

  clearSelectImageIds(): void {
    this._selectedImageIds = [];
  }

  moveImageLocationObs(): Observable<MoveImageIDLocation> {
    return this.onMoveImageLocation$;
  }

  filterLocationObs(): Observable<SubjectLocation> {
    return this.onFilterLocation$;
  }

  reuseReportObs(): Observable<string> {
    return this.onReuseReport$;
  }

  changeReportObs(): Observable<ViewReport> {
    return this.onChangeReport$;
  }

  viewImagesObs(): Observable<unknown> {
    return this.onViewImages$;
  }

  viewReportObs(): Observable<ViewReport> {
    return this.onViewReport$;
  }

  updateReportTabObs(): Observable<InspectionReport> {
    return this.onUpdateReportTab$;
  }

  reEditReportObs(): Observable<InspectionReport> {
    return this.onReEditReport$;
  }

  createDraftPdfObs(): Observable<GEN_DRAFT_REPORT_TYPE> {
    return this.onCreateDraftPdf$;
  }

  createFinalPdfObs(): Observable<GEN_FINAL_REPORT_TYPE> {
    return this.onCreateFinalPdf$;
  }

  changePastReportFilterObs(): Observable<string> {
    return this.onChangePastReportFilter$;
  }

  moveImageLocationEmit(location: SubjectLocation, imageIds = this._selectedImageIds) {
    this.onMoveImageLocation$.next(new MoveImageIDLocation(imageIds, location));
  }

  filterLocationEmit(location: SubjectLocation) {
    this.onFilterLocation$.next(location);
  }

  reuseReportEmit(reportId: string) {
    this.onReuseReport$.next(reportId);
  }

  changeReportEmit(viewReport: ViewReport) {
    this.onChangeReport$.next(viewReport);
  }

  viewImagesEmit() {
    this.onViewImages$.next(undefined);
  }

  viewReportEmit(viewReport: ViewReport) {
    this.onViewReport$.next(viewReport);
  }

  updateReportTabEmit(inspectionReport: InspectionReport) {
    this.onUpdateReportTab$.next(inspectionReport);
  }

  reEditReportEmit(inspectionReport: InspectionReport) {
    this.onReEditReport$.next(inspectionReport);
  }

  createDraftPdfEmit(type: GEN_DRAFT_REPORT_TYPE) {
    this.onCreateDraftPdf$.next(type);
  }

  createFinalPdfEmit(type: GEN_FINAL_REPORT_TYPE) {
    this.onCreateFinalPdf$.next(type);
  }

  changePastReportFilterEmit(filter: string) {
    this.onChangePastReportFilter$.next(filter);
  }

  public clearState() {
    this.onFilterLocation$.next(null);
  }
}
